// 261005 (07/10, F34) — chữ lỗi job cho NGƯỜI đọc. Hàm THUẦN, KHÔNG import antd / react.
//
// BE có thể ghi nguyên lỗi hệ thống vào `note` của job (ca thật: "Invalid `tx.filmLocalizeVersion.createMany()` invocation in
// /Users/…/filmLocalizeVersionRepository.ts:57:38 …") — lộ đường dẫn máy + code. FE tự phòng thủ bất kể BE: lỗi trông kỹ thuật
// thì chỉ hiện câu chung; chữ thô vẫn còn ở "Chi tiết kỹ thuật" của chặng (StageFrame).
// 3h5a lượt 2: chữ ĐỌC ĐƯỢC còn đi qua `redactMoney` (chỉ thay SỐ TIỀN, không đụng từ «trần», «Trần Minh», «xu hướng»…).

import { redactMoney } from './moneyRedact';

/** Câu chung khi lỗi quá kỹ thuật để đọc. */
export const GENERIC_JOB_ERROR_HINT = 'thử lại; nếu lặp lại báo kỹ thuật';

/** Note của job: quá dài ⇒ coi là dump. Lý do / câu lỗi API có thể dài hợp lệ (danh sách id) nên dùng trần rộng hơn. */
const MAX_READABLE_LEN = 200;
const MAX_READABLE_LEN_LOOSE = 600;

const TECHNICAL_PATTERNS: RegExp[] = [
  /\binvocation\b/i,                       // Prisma: "Invalid `x.y()` invocation"
  /prisma/i,
  /`[\w$.]+\([^)]*\)`/,                    // `tx.filmX.createMany()`
  /\.(ts|tsx|js|jsx|mjs|cjs):\d+/i,        // file.ts:57:38
  /(^|[\s("'])\/(Users|home|var|app|usr|opt|srv|root|tmp|workspace)\//,  // đường dẫn tuyệt đối unix
  /\b[A-Za-z]:\\/,                          // đường dẫn Windows
  /node_modules/i,
  /\n\s*at\s+\S+/,                          // stack trace
  /\b(TypeError|ReferenceError|SyntaxError|RangeError|Error:\s*connect|ECONN\w*|ETIMEDOUT|ENOTFOUND|EPIPE)\b/,
  /\bP\d{4}\b.*\b(constraint|Unique|Foreign)/i,
  /\n.*\n.*\n/,                            // ≥ 3 dòng ⇒ dump
  // Lỗi parse/giao thức của lớp AI (ca thật F40: "Failed text2Json after 2 retries: Unterminated string in JSON at position 6253 (line 211 column 17)")
  /\bjson\b/i, /text2json|structuredjson/i, /\bafter \d+ retr(y|ies)\b/i, /\bat position \d+/i, /\bline \d+,? column \d+/i,
  /\bUnexpected (token|end)\b/i, /\bUnterminated\b/i, /\bFailed [a-z]\w*\b.*\b(retries|attempts)\b/i,
  /\b(timeout|timed out|socket hang up|ECONN\w*|ENOTFOUND)\b/i,
];

/** Lỗi có "trông kỹ thuật" không (đường dẫn, mã Prisma, stack, quá dài…). Rỗng/null ⇒ false. */
export const looksTechnical = (note: string | null | undefined, opts: { loose?: boolean } = {}): boolean => {
  const s = (note ?? '').toString().trim();
  if (!s) return false;
  if (s.length > (opts.loose ? MAX_READABLE_LEN_LOOSE : MAX_READABLE_LEN)) return true;
  return TECHNICAL_PATTERNS.some((re) => re.test(s));
};

/**
 * Chữ lỗi job để HIỆN cho người dùng: rỗng ⇒ ''; đọc được ⇒ giữ nguyên (đã trim); kỹ thuật ⇒ câu chung.
 * Áp cho mọi loại job (nạp/quét/dựng/bản địa hoá/chia tập/viết).
 */
export const friendlyJobNote = (note: string | null | undefined): string => {
  const s = (note ?? '').toString().trim();
  if (!s) return '';
  return looksTechnical(s) ? GENERIC_JOB_ERROR_HINT : redactMoney(s);
};

/** Toast khi job nền lỗi: chữ đọc được đầu tiên của BE (error rồi note); chỉ có chữ kỹ thuật ⇒ câu chung; không có gì ⇒ "Job thất bại.". */
export const jobFailedToast = (error: string | null | undefined, note: string | null | undefined): string => {
  const cands = [error, note].map((x) => (x ?? '').toString().trim()).filter(Boolean);
  const readable = cands.find((x) => !looksTechnical(x));
  if (readable) return redactMoney(readable);
  return cands.length ? `Job thất bại — ${GENERIC_JOB_ERROR_HINT}.` : 'Job thất bại.';
};

/**
 * Lý do "Cần bạn xem" / chặn (BE ghép `Lần chạy bản địa hoá gần đây lỗi: <note thô>`): giữ phần đầu người đọc + câu chung,
 * chữ thô để ở "Chi tiết kỹ thuật". Không kỹ thuật ⇒ giữ nguyên.
 */
export const friendlyReasonText = (message: string | null | undefined): string => {
  const s = (message ?? '').toString().trim();
  if (!looksTechnical(s, { loose: true })) return redactMoney(s);
  const i = s.indexOf(':');
  const head = i > 0 ? s.slice(0, i).trim() : '';
  return head && head.length <= 100 && !looksTechnical(head, { loose: true }) ? `${redactMoney(head)}: ${GENERIC_JOB_ERROR_HINT}` : `Có lỗi — ${GENERIC_JOB_ERROR_HINT}`;
};

/** Câu lỗi API đưa lên toast: đọc được ⇒ giữ; rỗng ⇒ `fallback`; kỹ thuật ⇒ `fallback — <câu chung>`. */
export const friendlyApiMessage = (raw: string | null | undefined, fallback: string): string => {
  const s = (raw ?? '').toString().trim();
  if (!s) return fallback;
  return looksTechnical(s, { loose: true }) ? `${fallback} — ${GENERIC_JOB_ERROR_HINT}` : redactMoney(s);
};

/** Như `friendlyApiMessage` nhưng lấy từ lỗi axios: `response.data.message` hoặc `messages[]` (restInterceptor). */
export const friendlyApiError = (err: unknown, fallback: string): string => {
  const d = (err as { response?: { data?: { message?: unknown; messages?: unknown } } } | null | undefined)?.response?.data;
  const raw = typeof d?.message === 'string' ? d.message
    : Array.isArray(d?.messages) ? (d?.messages as unknown[]).filter((x) => typeof x === 'string' && x).join(' · ') : '';
  return friendlyApiMessage(raw, fallback);
};

/** Dòng ở modal «Chi tiết kỹ thuật»: giữ chữ thô (để người báo kỹ thuật dùng) nhưng bỏ SỐ TIỀN — người dùng không thấy tiền ở bất kỳ chỗ nào của màn Chuyển thể. */
export const technicalDetailText = (raw: string | null | undefined): string => redactMoney(raw);
